import { get, post, postFormRaw, postRaw } from 'services/api';
// import { StartNodeData, Workflow } from 'modules/workflows/types';
import { useRestQuery } from 'utils/hooks/api';
import client from './ApolloClient';
import { LIST_FIELD_FACETS } from './ListsApi';
import {
  cancelJourneyCampaignAlertsMutation,
  createWorkflowDraftMutation,
  deleteWorkflowDraftMutation,
  rescheduleJourneyCampaignAlertsMutation,
  saveWorkflowDraftMutation,
} from './mutations/workflows';
import {
  fetchClonedFromJourney,
  fetchSendNodeResourcesQuery,
  fetchWorkflowCampaignNodesQuery,
  fetchWorkflowQueryV2,
  validateMultiContent,
  getJourneyVisitorMetrics,
} from './queries';

/* GRAPHQL ENDPOINTS */

// export const getMessageChannels = () => client.query({ query: fetchMessageChannelsQuery });

export const getSendNodeResources = ({ campaignId, channel }) => {
  return client.query({
    query: fetchSendNodeResourcesQuery,
    variables: {
      campaignId: campaignId || -1,
      hasCampaign: !!campaignId,
      withMobileApps: !!campaignId && channel === 'push',
    },
  });
};

/**
 * Includes `workflowType` and `workflowReference` for
 * workflow drafts.
 */
export const getWorkflowByIdV2 = (workflowId, workflowType) =>
  client.query({ query: fetchWorkflowQueryV2, variables: { id: workflowId, workflowType } });

export const getClonedFromJourney = (workflowId, workflowType) =>
  client.query({ query: fetchClonedFromJourney, variables: { id: workflowId, workflowType } });

export const getValidateMultiContent = (templateIds) =>
  client.query({ query: validateMultiContent, variables: { templateIds } });
export const getJourneyVisitorCounts = (workflowId) =>
  client.query({ query: fetchVisitorCounts, variables: { workflowId } });

export const saveWorkflowDraft = (workflow /*: Workflow */) =>
  client.mutate({
    mutation: saveWorkflowDraftMutation,
    variables: { workflow: { json: workflow } },
  });

export const createWorkflowDraft = (workflow /*: Workflow */) =>
  client.mutate({
    mutation: createWorkflowDraftMutation,
    variables: { workflow: { json: workflow } },
  });

export const deleteWorkflowDraft = (id /* : number */) =>
  client.mutate({
    mutation: deleteWorkflowDraftMutation,
    variables: { id },
  });

export const getWorkflowCampaignNodes = (workflowId, workflowType, nodeResources) =>
  client.query({
    query: fetchWorkflowCampaignNodesQuery,
    variables: { workflowId, workflowType, nodeResources },
  });

export const cancelJourneyCampaignAlerts = (journeyIds) =>
  client.mutate({
    mutation: cancelJourneyCampaignAlertsMutation,
    variables: { journeyIds },
  });

export const rescheduleJourneyCampaignAlerts = (journeyIds) =>
  client.mutate({
    mutation: rescheduleJourneyCampaignAlertsMutation,
    variables: { journeyIds },
  });

export const fetchJourneyVisitorMetrics = (workflowId, startTime, endTime) =>
  client.query({
    query: getJourneyVisitorMetrics,
    variables: {
      workflowId,
      startTime: startTime,
      endTime: endTime,
    },
  });

// export const getFacebookAdAccountIds = () => client.query({ query: fetchFacebookAdAccountIdsQuery });

/* GRAPHQL STUBS */

export const getBuiltInEvents = () => ({
  data: { builtInEvents: { 1: { id: 1, name: 'event' } } },
});

export const getCampaignById = (_id) => ({
  data: { campaign: { id: 1, name: 'resource' } },
});

export const getCampaignLabels = () => ({
  data: { campaignLabels: { 1: { id: 1, name: 'resource' } } },
});

export const getCampaigns = () => ({
  data: { campaigns: { 1: { id: 1, name: 'resource' } } },
});

export const getContactProperties = () => ({
  data: { contactProperties: { 1: { id: 1, name: 'property' } } },
});

export const getFilterProperties = () => ({
  data: { filterProperties: { 1: { id: 1, name: 'property' } } },
});

export const getMessageChannels = () => ({
  data: {
    messageChannels: [
      {
        id: 1,
        name: 'channel',
        creatorUserId: 'test@iterable.com',
        updatedAt: '2020-07-14T15:13:25.655-07:00',
      },
      {
        id: 2,
        name: 'channel2',
        creatorUserId: 'test@iterable.com',
        updatedAt: '2020-07-14T15:13:25.655-07:00',
      },
    ],
  },
});

export const getMessageTypes = () => ({
  data: { messageTypes: [{ id: 1 }] },
});

export const getTemplates = (_channel) => ({
  data: {
    templates: {
      1: {
        id: 1,
      },
    },
  },
});

export const getWorkflows = () => ({
  data: {
    workflows: {
      1: {
        id: 1,
      },
    },
  },
});

export const getWorkflowWebhooks = () => ({
  data: {
    workflowWebhooks: {
      1: {
        id: 1,
      },
    },
  },
});

/* AXIOS ENDPOINTS */

/* Existing Endpoints */
export const CLONE_WORKFLOW_URL = '/workflows/clone';
export const CREATE_WORKFLOW_URL = '/workflows/createWithParams';
export const DELETE_WORKFLOWS_URL = '/workflows/archive';
export const UNARCHIVE_WORKFLOWS_URL = '/workflows/unarchive';
export const FETCH_BETA_NODES_URL = '/i/workflows/betaNodes';
export const FETCH_VISITOR_COUNTS_URL = '/workflows/visitorCounts';
export const FETCH_VISITOR_COUNTS_FOR_DRAFTS_URL = '/workflows/draftVisitorCounts';
export const FETCH_CONCISE_VISITOR_COUNTS_URL = '/workflows/conciseVisitorCounts';
export const SAVE_WORKFLOW_URL = '/workflows/save';
export const PUBLISH_WORKFLOW_DRAFT_URL = '/workflows/publish';
export const SEND_TEST_TRIGGER_URL = '/workflows/testTrigger';
export const UPDATE_WORKFLOW_DETAILS_URL = '/workflows/editWorkflowName';
export const UPDATE_WORKFLOW_STATUS_URL = '/workflows/updateEnabled';
export const UPSERT_WORKFLOW_CAMPAIGN = '/workflows/upsertWorkflowCampaign';
export const VALIDATE_WORKFLOW_URL = '/workflows/validateWorkflow';

export const FETCH_EVENT_FIELD_MAPPINGS_URL = '/i/workflows/eventFieldMappings';
export const GET_CUSTOM_EVENT_MAPPINGS_URL = '/mappings/customEventMappings';
export const GET_TOP_LEVEL_CUSTOM_EVENTS_MAPPINGS_URL = '/mappings/customEvents';
export const GET_USER_MAPPINGS_URL = '/mappings/userMappings';

export const STATS_FOR_TIMERANGE_URL = '/analytics/getStatsForTimeRange';

export const FETCH_FB_CUSTOM_AUDIENCE_URL = '/i/facebookCustomAudience';

export const FIX_STUCK_USERS_URL = '/workflows/fixStuckWorkflowUsers';
export const EXPORT_STUCK_USERS_URL = '/workflows/exportStuckWorfklowUsers';

export const COPY_TEMPLATE_TO_CAMPAIGN_URL = '/templates/copyTemplateToCampaign';
export const GET_TEMPLATE_ID_FROM_CAMPAIGN_URL = '/templatesCampaign';

export const CREATE_LIST_IMPORT_SUBMIT_URL = '/lists/importSubmit';

export const createWorkflow = (
  name /* : string */,
  description /* : string */,
  workflowSimultaneousLimit /* : number | null */,
  workflowLifetimeLimit /* : number | null, */,
) => {
  const payload = {
    name,
    description,
    workflowSimultaneousLimit,
    workflowLifetimeLimit,
  };
  return get(CREATE_WORKFLOW_URL, payload);
};
export const saveWorkflow = (workflow /*: Workflow */) /* : Promise<{ workflowId: number }> */ =>
  post(SAVE_WORKFLOW_URL, workflow);

export const publishWorkflowDraft = (
  workflow /*: Workflow */,
) /* : Promise<{ workflowId: number }> */ => post(PUBLISH_WORKFLOW_DRAFT_URL, workflow);

export const validateWorkflow = (workflow /*: Workflow */) => post(VALIDATE_WORKFLOW_URL, workflow);

export const cloneWorkflow = (
  params /*: {
  workflowId: number,
  workflowType?: string,
  destinationFolderId?: number,
} */,
) => post(CLONE_WORKFLOW_URL, params);

export const deleteWorkflows = (
  workflowIds /* : number[], */,
) /* : Promise<{ success: number[], fail: number[] }> */ =>
  post(DELETE_WORKFLOWS_URL, { workflowIds });

export const unarchiveWorkflows = (
  workflowIds /* : number[], */,
) /* : Promise<{ success: number[], fail: number[] }> */ =>
  post(UNARCHIVE_WORKFLOWS_URL, { workflowIds });

export const fetchCustomEventMappings = () => get(`${GET_CUSTOM_EVENT_MAPPINGS_URL}`);

export const fetchUserMappings = () => get(`${GET_USER_MAPPINGS_URL}`);

export const fetchTopLevelCustomEvents = () => get(`${GET_TOP_LEVEL_CUSTOM_EVENTS_MAPPINGS_URL}`);

export const fetchBetaNodes = () => get(FETCH_BETA_NODES_URL);

export const fetchVisitorCounts = (workflowId /* : number */) =>
  get(`${FETCH_VISITOR_COUNTS_URL}?workflowId=${workflowId}`);

export const fetchVisitorCountsForDrafts = (workflowId /* : number */) =>
  get(`${FETCH_VISITOR_COUNTS_FOR_DRAFTS_URL}?workflowId=${workflowId}`);

export const fetchConciseVisitorCounts = (workflowIds /* : number[] */) =>
  get(
    `${FETCH_CONCISE_VISITOR_COUNTS_URL}?${workflowIds.map((id) => `workflowIds=${id}`).join('&')}`,
  );

export const fetchEventFieldMappings = () => get(FETCH_EVENT_FIELD_MAPPINGS_URL);
export const useEventFieldMappings = () => useRestQuery(FETCH_EVENT_FIELD_MAPPINGS_URL);

export const sendTestTrigger = (
  dataFields /*: object */,
  email /*: string */ = null,
  userId /*: string */ = null,
  overrideSendLimit /*: boolean */,
  triggerNode /*: StartNodeData */,
  workflowId /*: number */,
  workflow /* ?: null */,
) => {
  const payload = {
    ...(Object.keys(dataFields).length > 0 ? { dataFields } : null), // don't include parameter if no data fields given
    email,
    userId,
    overrideSendLimit,
    triggerNode,
    workflowId,
    workflowType: workflow.workflowType,
  };
  return post(SEND_TEST_TRIGGER_URL, payload);
};

export const updateWorkflowDetails = (
  workflowId /*: number */,
  details /*: {
    description?: string,
    name: string,
  } */,
) => {
  const payload = { workflowId, ...details };
  return post(UPDATE_WORKFLOW_DETAILS_URL, payload);
};

export const updateWorkflowStatus = (
  workflowIds /* : Array<number> */,
  enabled /* : boolean */,
) => {
  const payload = { ids: workflowIds, enabled };
  return post(UPDATE_WORKFLOW_STATUS_URL, payload);
};

export const fetchFieldFacets = (
  field /* : string */,
  dataType /* : string */,
) /* : Promise<{ term: string, count: number }[]> */ =>
  post(LIST_FIELD_FACETS, { field, dataType });

export const fetchSendNodeAnalytics = (queryData) => post(STATS_FOR_TIMERANGE_URL, queryData);

export const fetchFacebookCustomAudience = () => get(FETCH_FB_CUSTOM_AUDIENCE_URL);

export const fixStuckUsers = (payload) => post(FIX_STUCK_USERS_URL, payload);

export const exportStuckUsers = (workflowId /*: number */, safeDeleteDays) =>
  get(`${EXPORT_STUCK_USERS_URL}?workflowId=${workflowId}&safeDeleteDays=${safeDeleteDays}`);

export const copyTemplateToWorkflowCampaign = (campaignId, templateId) =>
  postRaw(COPY_TEMPLATE_TO_CAMPAIGN_URL, {
    campaignId,
    templateId,
  });

export const createListFromTitle = (listTitle /* : string */, projectId /*: number */) => {
  const payload = {
    projectId,
    name: listTitle,
    description: '',
    emailListType: 'Standard',
    sendContactAddEvent: false,
    updateExistingUsersOnly: false,
    csvFile: '',
  };
  return postFormRaw(CREATE_LIST_IMPORT_SUBMIT_URL, payload);
};
